import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { ifcElementTypeLabels } from '@editor/components/Controls/IFCUploadWidget/constants/ifcFilter'
import { useCameraStore } from '@modugen/ui-toolkit'
import { CorneredSection } from '@ui2/structure'
import { useControlStore, useEditElementStore, useSelectionStore } from '@editorStores'
import { useScopedSelectionReset, useTranslucentGltfModel } from '@editorHooks'
import useIFCImportWallDrawing from '../../hooks/useIFCImportWallDrawing'
import IFCImportToolsSection from '../IFCImportToolsSection'
import IFCPlanarElementForm from '../IFCPlanarElementForm'
import IFCWallAutoConnectSection from '../IFCWallAutoConnectSection'

/** Sub-tools of the wall edit steps — canvas clicks belong to one tool at a time. */
type WallEditTool = 'edit' | 'draw' | 'auto'

const typeListFormat = new Intl.ListFormat('de', { type: 'conjunction' })

const WALL_TYPES: ElementTypes[] = ['outer_walls', 'inner_walls']

interface Props {
  /**
   * Planar element types to edit, e.g. `['roof_slabs']` or `['outer_walls', 'inner_walls']` (inner
   * and outer walls are edited in one step).
   */
  elementTypes: ElementTypes[]
}

/**
 * Edit step of the guided IFC import for one or more planar element types: activates planar-model
 * selection restricted to those types while shown, so clicking an element in the scene opens its
 * Structural Planning edit form (Bauteil tab). The wall edit step additionally offers a "Zeichnen"
 * tool that draws missing walls in the orthographic floorplan view (persisted immediately, with the
 * placement picked in the tool) and an "Automatisch" tool that displays a proposal for connecting
 * nearby wall ends in the floorplan; both are exclusive with selection.
 */
const IFCElementEditPanel = ({ elementTypes }: Props): ReactElement => {
  const wallTypes = useMemo(
    () => elementTypes.filter(type => WALL_TYPES.includes(type)),
    [elementTypes],
  )
  const isWallStep = wallTypes.length > 0
  const [tool, setTool] = useState<WallEditTool>('edit')
  const isDrawing = isWallStep && tool === 'draw'
  const isAutoConnect = isWallStep && tool === 'auto'

  // Placement drawn walls are created with. Fixed when the step covers a single wall type,
  // otherwise picked in the "Zeichnen" tool (defaulting to external).
  const [selectedDrawPlacement, setSelectedDrawPlacement] = useState<WallPlacement>('External')
  const drawPlacement =
    wallTypes.length === 1
      ? wallTypes[0] === 'outer_walls'
        ? 'External'
        : 'Internal'
      : selectedDrawPlacement

  const storey = useControlStore(state => state.floorplanState.storey)
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)
  const setPlanarSelectableTypes = useControlStore(state => state.setPlanarSelectableTypes)
  const activeElement = useEditElementStore(state => state.activeElement)
  const multiActiveElements = useEditElementStore(state => state.multiActiveElements)
  const setIsOrthographic = useCameraStore(state => state.setIsOrthographic)
  const clearSelection = useSelectionStore(state => state.clearSelection)

  // While drawing or viewing the automatic connection proposal, planar selection is restricted to
  // no types so canvas clicks don't open edit forms; otherwise it is restricted to the step's
  // element types.
  const selectableTypes = useMemo<ElementTypes[]>(
    () => (isDrawing || isAutoConnect ? [] : elementTypes),
    [isDrawing, isAutoConnect, elementTypes],
  )

  // The import helper is offered for the wall edit steps while no wall is selected and the edit
  // tool is active; the tools section itself (with the transparency switch) is always shown.
  const showImportHelper = isWallStep && !isDrawing && !isAutoConnect && !activeElement

  // Render the GLB translucent while editing so the planar elements stay visible.
  useTranslucentGltfModel()

  // "Zeichnen" tool: drawn walls are persisted right away with the tool's placement.
  useIFCImportWallDrawing(isDrawing, drawPlacement)

  // Restrict planar-model selection to these types while the step is shown (the guided drawer owns
  // the selection mode itself); the step's own selection is reset on enter and leave.
  useEffect(() => {
    setPlanarSelectableTypes(selectableTypes)

    return () => setPlanarSelectableTypes(null)
  }, [selectableTypes, setPlanarSelectableTypes])
  useScopedSelectionReset()

  const typeList = typeListFormat.format(
    elementTypes.map(type => `„${ifcElementTypeLabels[type] ?? type}“`),
  )

  return (
    <Stack spacing={2}>
      {isWallStep && (
        <CorneredSection title="Werkzeug">
          <Stack spacing={2}>
            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={tool}
              onChange={(_event, value: WallEditTool | null) => {
                if (!value) return
                setTool(value)
                // Each tool starts fresh: the previous tool's element selection does not carry
                // over (e.g. Bearbeiten → Zeichnen → Bearbeiten must not resurface the old wall).
                clearSelection()
                // The wall drawer operates in the orthographic floorplan view: enter it when
                // the tool is activated (keeping the shown storey, defaulting to storey 0).
                // The view deliberately persists when the tool is left so switching between
                // the tools doesn't reset the selected storey. The "Automatisch" tool instead
                // stays in the current view until a storey is picked in its dropdown (see
                // IFCWallAutoConnectSection).
                if (value === 'draw') {
                  if (!storey) setFloorplanStorey('0')
                  setIsOrthographic(true)
                }
              }}
              sx={{ '& .MuiToggleButton-root': { py: 0.25 } }}
            >
              <ToggleButton value="edit" data-cy="btn-ifc-wall-draw-tool-edit">
                Bearbeiten
              </ToggleButton>
              <ToggleButton value="draw" data-cy="btn-ifc-wall-draw-tool-draw">
                Zeichnen
              </ToggleButton>
              <ToggleButton value="auto" data-cy="btn-ifc-wall-draw-tool-auto">
                Automatisch
              </ToggleButton>
            </ToggleButtonGroup>
            {/* With both wall types in one step the drawn wall's placement is picked here. */}
            {isDrawing && wallTypes.length > 1 && (
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={selectedDrawPlacement}
                onChange={(_event, value: WallPlacement | null) =>
                  value && setSelectedDrawPlacement(value)
                }
                sx={{ '& .MuiToggleButton-root': { py: 0.25 } }}
              >
                <ToggleButton value="External" data-cy="btn-ifc-wall-draw-placement-external">
                  Außenwand
                </ToggleButton>
                <ToggleButton value="Internal" data-cy="btn-ifc-wall-draw-placement-internal">
                  Innenwand
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {isAutoConnect ? (
              <IFCWallAutoConnectSection />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {isDrawing
                  ? `Ziehen Sie im Grundriss, um eine neue Wand zu zeichnen. Die Wand wird direkt als ${
                      drawPlacement === 'External' ? 'Außenwand' : 'Innenwand'
                    } gespeichert.`
                  : 'Wählen Sie eine Wand im Modell aus, um sie zu verschieben oder zu bearbeiten.'}
              </Typography>
            )}
          </Stack>
        </CorneredSection>
      )}

      {!isDrawing &&
        !isAutoConnect &&
        (!isWallStep || activeElement || multiActiveElements.length > 0) && (
          <IFCPlanarElementForm
            placeholder={`Element vom Typ ${typeList} im Modell auswählen, um es zu bearbeiten.`}
          />
        )}

      <IFCImportToolsSection showImportHelper={showImportHelper} />
    </Stack>
  )
}

export default IFCElementEditPanel
